import React, { useState, useEffect } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { OmMenu, type OmMenuItem, type OmMenuClickInfo } from '@owma/kit'
import { Link } from 'react-router'
import { withRouter } from 'router'
import classNames from 'classnames'
import localforage from 'myStore'
import { find } from 'lodash'
import styles from './style.module.scss'

interface WithRouterProps {
  location: {
    pathname: string
    search?: string
    hash?: string
    state?: any
  }
  history?: any
  params?: any
  routes?: any
}

interface MenuItem {
  key: string
  title: string
  url?: string
  icon?: string
  disabled?: boolean
  category?: boolean
  count?: number
  children?: MenuItem[]
  roles?: string[]
  target?: string
}

const mapStateToProps = ({ menu, appsettings, user }: any) => ({
  menuData: menu.menuData as MenuItem[],
  logo: appsettings.logo as string,
  menuColor: appsettings.menuColor as string,
  role: user.role as string,
})

const connector = connect(mapStateToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

interface MenuTopProps extends PropsFromRedux, WithRouterProps {}

const MenuTop: React.FC<MenuTopProps> = ({
  menuData = [],
  location: { pathname },
  menuColor,
  logo,
  role,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])

  useEffect(() => {
    const initSelectedKeys = async () => {
      const storedKeys = (await localforage.getItem('app.menu.selectedKeys')) as string[] | null
      setSelectedKeys(storedKeys || [])
    }
    initSelectedKeys()
  }, [])

  useEffect(() => {
    applySelectedKeys()
  }, [pathname, menuData])

  const applySelectedKeys = () => {
    const flattenItems = (items: MenuItem[], key: string): MenuItem[] =>
      items.reduce((flattenedItems: MenuItem[], item: MenuItem) => {
        flattenedItems.push(item)
        if (Array.isArray(item[key as keyof MenuItem])) {
          return flattenedItems.concat(flattenItems(item[key as keyof MenuItem] as MenuItem[], key))
        }
        return flattenedItems
      }, [])
    const selectedItem = find(flattenItems(menuData, 'children'), ['url', pathname])
    setSelectedKeys(selectedItem ? [selectedItem.key] : [])
  }

  const handleClick = async (e: OmMenuClickInfo) => {
    await localforage.setItem('app.menu.selectedKeys', [e.key])
    setSelectedKeys([e.key])
  }

  const generateMenuItems = (): OmMenuItem[] => {
    const renderContent = (item: MenuItem): React.ReactNode => (
      <React.Fragment>
        {item.icon && <span className={`${item.icon} ${styles.icon}`} />}
        <span className={styles.title}>{item.title}</span>
        {item.count && <span className="badge badge-success ml-2">{item.count}</span>}
      </React.Fragment>
    )

    const generateItem = (item: MenuItem): OmMenuItem | null => {
      const { key, url, disabled } = item
      if (item.category) {
        return null
      }
      let label: React.ReactNode = renderContent(item)
      if (url) {
        label = item.target ? (
          <a href={url} target={item.target} rel="noopener noreferrer">
            {renderContent(item)}
          </a>
        ) : (
          <Link to={url}>{renderContent(item)}</Link>
        )
      }
      return { key, disabled, label }
    }

    const generateSubmenu = (items: MenuItem[]): OmMenuItem[] =>
      items
        .map((menuItem): OmMenuItem | null => {
          if (menuItem.children) {
            return {
              key: menuItem.key,
              label: renderContent(menuItem),
              children: generateSubmenu(menuItem.children),
            }
          }
          return generateItem(menuItem)
        })
        .filter((it): it is OmMenuItem => it != null)

    return menuData
      .map((menuItem): OmMenuItem | null => {
        if (menuItem.roles && !menuItem.roles.includes(role)) {
          return null
        }
        if (menuItem.children) {
          return {
            key: menuItem.key,
            label: renderContent(menuItem),
            children: generateSubmenu(menuItem.children),
          }
        }
        return generateItem(menuItem)
      })
      .filter((it): it is OmMenuItem => it != null)
  }

  return (
    <div
      className={classNames(`${styles.menu}`, {
        [styles.white]: menuColor === 'white',
        [styles.gray]: menuColor === 'gray',
        [styles.dark]: menuColor === 'dark',
      })}
    >
      <div className={styles.logoContainer}>
        <div className={styles.logo}>
          <img src="resources/images/logo.svg" className="mr-2" alt={logo} />
          <div className={styles.name}>{logo}</div>
          {/* logo === 'Clean UI Pro' && <div className={styles.descr}>React</div> */}
        </div>
      </div>
      <div className={styles.navigation}>
        <OmMenu
          onClick={handleClick}
          selectedKeys={selectedKeys}
          mode="horizontal"
          items={generateMenuItems()}
        />
      </div>
    </div>
  )
}

export default withRouter(connector(MenuTop))
